/* eslint-disable prefer-promise-reject-errors */
import { fabric } from 'fabric'
import throttle from 'lodash/throttle'
import db from './db'
import history from './history'
import objects from './objects'
import upload from './upload'
import fileType from '@/libs/file-type'
import { v4 as uuidv4 } from 'uuid'

let canvas = null

let shapeMode = ''
let shape = null

let snap = false

let lastUpdate = null
let readOnly = false
let doneLoading = false
let objectStatus = null
let filtered = false

let objectQueue = []
let queueStarted = false
let waitForImages = false
let newBoard = false

let clickMenu = null

let timers = {}

let delayedStyleSettings = []

let bgMinZoom = 1

const defaults = {
  colour: '#000000',
  strokeWidth: 3,
  fontSize: 30,
  align: 'center'
}

export default {
  id: null,
  expectedNotLoaded: 1234567890,
  fill: { colour: defaults.colour, none: true },
  stroke: { colour: defaults.colour, width: defaults.strokeWidth },
  text: { colour: defaults.colour, size: defaults.fontSize, bold: false, italic: false, underline: false, serif: false, align: defaults.align },
  init: function (config) {
    return new Promise((resolve, reject) => {
      this.id = config.boardKey

      if (canvas) {
        canvas.clear()
        canvas.off()
        canvas = null
      }

      canvas = new fabric.Canvas(config.id, {
        backgroundColor: '#FFFFFF',
        isDrawingMode: false,
        preserveObjectStacking: true, // prevents selected object from moving to foreground
        allowTouchScrolling: true // mobile freeze work-around
      })

      fabric.Image._fromObjectMode = 2

      snap = !!config.snap

      history.reset()
      lastUpdate = null
      doneLoading = false

      readOnly = config.readOnly
      objectStatus = config.objectStatus
      clickMenu = config.clickMenu

      objectQueue = []
      queueStarted = false
      waitForImages = !!config.waitForImages
      newBoard = !!config.new

      this.resetTimers()

      db.init({ id: config.boardKey, board: this, history: history, readOnly: readOnly, objectStatus: config.objectStatus }).then(() => {
        canvas.on('object:added', (options) => {
          this.objectAdded(options.target)
        })

        canvas.on('object:modified', (options) => {
          this.objectModified(options.target)
        })

        canvas.on('object:scaling', (options) => {
          this.objectScaling(options.target)
        })

        canvas.on('object:scaled', (options) => {
          this.objectScaled(options.target)
        })

        canvas.on('selection:created', (options) => {
          this.objectSelected(options.target, config.styleSettings, config.configureToolbar)
        })

        canvas.on('selection:updated', (options) => {
          this.objectSelected(options.target, config.styleSettings, config.configureToolbar)
        })

        canvas.on('object:moving', (options) => {
          this.objectMoving(options.target)
        })

        canvas.on('object:rotating', (options) => {
          this.objectRotating(options.target, options.e)
        })

        canvas.on('selection:cleared', () => {
          if (canvas.selection) {
            config.configureToolbar('Select')
          }
        })

        if (readOnly) {
          this.readOnly()
        } else {
          canvas.on('mouse:down', (options) => {
            if (shapeMode) {
              this.startShape(options.e)
            }
          })

          canvas.on('mouse:up', (options) => {
            if (shapeMode && shape) {
              this.endShape(options.e)
            } else if (shapeMode === 'erase') {
              this.erase()
            }
          })

          canvas.on('mouse:move', (options) => {
            if (shapeMode && shape) {
              this.updateShape(options.e)
            }
          })

          fabric.util.addListener(canvas.upperCanvasEl, 'dblclick', (e) => {
            this.handleDoubleClick(canvas.findTarget(e))
          })
          fabric.util.addListener(canvas.upperCanvasEl, 'touchstart', (e) => {
            if (timers.touchStart) {
              this.handleDoubleClick(canvas.findTarget(e))
            }
            timers.touchStart = setTimeout(() => { timers.touchStart = null }, 700)
          })

          this.setMode('')
        }

        resolve()
      }, () => {
        reject()
      })
    })
  },

  isLoaded: function () {
    return doneLoading
  },

  redraw: function () {
    canvas.renderAll()
  },

  stopListeners: function () {
    if (canvas) {
      canvas.off()
    }
    db.stopListeners()
  },

  setSize: function (width, height) {
    if (canvas) {
      canvas.setWidth(width)
      canvas.setHeight(height)
    }
  },

  customProperties: function () {
    return ['padding', 'linkURL', 'actionKey', 'key', 'parentKey', 'customType', 'position', 'fromTemplate', 'selectable', 'isBackground', 'crossOrigin']
  },

  historyProperties: function () {
    return ['padding', 'linkURL', 'customType', 'position', 'fromTemplate', 'selectable']
  },

  json: function (object) {
    return object.toJSON(this.customProperties())
  },

  addParentKey: function (object) {
    if (object.type === 'group' && object.customType) {
      object.forEachObject((o) => {
        o.key = ''
        o.parentKey = object.key
      })
    }
  },

  firstObjectPosition: function () {
    if (canvas._objects.length === 0) {
      return 0
    }
    return canvas._objects[0].position || 0
  },

  lastObjectPosition: function () {
    if (canvas._objects.length === 0) {
      return 0
    }

    let i = canvas._objects.length - 1
    if (i > 0 && (!canvas._objects[i].key || !canvas._objects[i].key === db.ignoreKey)) {
      i--
    }

    return canvas._objects[i].position || 0
  },

  objectAdded: function (object) {
    lastUpdate = new Date()
    objectStatus.added++
    if (!object.key) {
      // prevent pencil "dabs"
      if (object.type === 'path' && !objects.shapes.visible(object)) {
        canvas.remove(object)
        return
      }

      if (object.position === undefined) {
        object.position = this.lastObjectPosition() + 1
      }

      const json = this.json(object)
      db.add(json)
      object.key = json.key
      object.actionKey = json.key
    }

    if (object.key !== db.ignoreKey) {
      object.id = object.key
    }

    if (readOnly) {
      this.makeObjectReadOnly(object)
    }
  },

  objectModified: function (object) {
    // this is a work-around so that the text in a sticky note doesn't trigger an update
    if (object.isNew) {
      return
    }

    lastUpdate = new Date()

    if (object.type === 'activeSelection') {
      object.clone((group) => {
        group.forEachObject((o) => {
          group.realizeTransform(o)
          db.update(this.json(o))
        })
      }, this.customProperties())
    } else {
      if (!object.key && !object.parentKey) {
        return
      }

      if (object.parentKey) {
        object = this.findObject({ type: 'group', key: object.parentKey })
      }

      db.update(this.json(object))
    }

    if (readOnly) {
      this.makeObjectReadOnly(object)
    }
  },

  objectScaling: function (object) {
    if (object.type === 'activeSelection') {
      object.forEachObject((o) => {
        this.objectScaling(o)
      })
    }

    if (objects.note.isA(object)) {
      objects.note.scale(object)
    }

    if (objects.shapes.isA(object)) {
      objects.shapes.preserveStrokeWidth(object)
    }
  },
  objectScaled: function (object) {
    if (object.type === 'activeSelection') {
      // scale objects within selection
      object.forEachObject((o) => {
        o.scaleX = object.scaleX
        o.scaleY = object.scaleY
        this.objectScaled(o)
        o.set({
          left: o.left * object.scaleX,
          top: o.top * object.scaleY
        })
      })

      // remove scale from active selection
      object.set({
        width: object.width * object.scaleX,
        height: object.height * object.scaleY,
        scaleX: 1,
        scaleY: 1
      })
    }

    if (objects.note.isA(object)) {
      objects.note.scale(object)
    }

    if (objects.shapes.isA(object)) {
      objects.shapes.scale(object)
    }
  },

  objectSelected: function (object, styleSettings, configureToolbar) {
    if (!shapeMode) {
      this.updateStyleSettings(styleSettings)
      configureToolbar('', true)
      this.unlockObject(object)
    }

    object.set({ cornerStyle: 'circle' })

    if (objects.shapes.isA(object)) {
      objects.shapes.configureControls(object)
    } else if (objects.text.isA(object)) {
      objects.text.configureControls(object)
    }
  },

  objectMoving: function (object) {
    if (snap) {
      this.snapToNearbyObjects(object)
    }
  },

  objectRotating: function (object, event) {
    if (event && event.shiftKey) {
      const angle = Math.round(object.angle / 45) * 45
      if (angle !== object.angle) {
        object.rotate(angle)
      }
    }
  },

  snapToNearbyObjects: function (object) {
    const b1 = object.getBoundingRect(true, true)
    b1.right = b1.left + b1.width
    b1.bottom = b1.top + b1.height

    const objDist = 250
    const snapDist = Math.max(10 / canvas.getZoom(), 10)

    let left = null
    let right = null
    let top = null
    let bottom = null

    canvas.forEachObject((o) => {
      if (o.key !== object.key && !((left || right) && (top || bottom))) {
        const b2 = o.getBoundingRect(true)
        b2.right = b2.left + b2.width
        b2.bottom = b2.top + b2.height

        const dist = {
          ll: Math.abs(b1.left - b2.left),
          lr: Math.abs(b1.left - b2.right),
          rr: Math.abs(b1.right - b2.right),
          rl: Math.abs(b1.right - b2.left),
          tt: Math.abs(b1.top - b2.top),
          tb: Math.abs(b1.top - b2.bottom),
          bb: Math.abs(b1.bottom - b2.bottom),
          bt: Math.abs(b1.bottom - b2.top),
          inL: b2.left < b1.left && b1.left < b2.right,
          inR: b2.left < b1.right && b1.right < b2.right,
          inT: b2.top < b1.top && b1.top < b2.bottom,
          inB: b2.top < b1.bottom && b1.bottom < b2.bottom,
          outL: b1.left < b2.left && b2.left < b2.right,
          outR: b1.left < b2.right && b2.right < b1.right,
          outT: b1.top < b2.top && b2.top < b1.bottom,
          outB: b1.top < b2.bottom && b2.bottom < b1.bottom
        }

        const inX = (dist.inL || dist.inR || dist.outL || dist.outR)
        const inY = (dist.inT || dist.inB || dist.outT || dist.ourB)
        const nearX = (dist.ll < objDist || dist.lr < objDist || dist.rl < objDist || dist.rr < objDist)
        const nearY = (dist.tt < objDist || dist.tb < objDist || dist.bt < objDist || dist.bb < objDist)

        if ((inY || nearY) && left === null && right === null) {
          if (dist.ll < snapDist) {
            left = b2.left
          } else if (dist.lr < snapDist) {
            left = b2.right
          } else if (dist.rr < snapDist) {
            right = b2.right
          } else if (dist.rl < snapDist) {
            right = b2.left
          }
        }

        if ((inX || nearX) && top === null && bottom === null) {
          if (dist.tt < snapDist) {
            top = b2.top
          } else if (dist.tb < snapDist) {
            top = b2.bottom
          } else if (dist.bb < snapDist) {
            bottom = b2.bottom
          } else if (dist.bt < snapDist) {
            bottom = b2.top
          }
        }
      }
    })

    if (left || right || top || bottom) {
      let x = b1.left + (b1.width / 2)
      if (left !== null) {
        x = left + (b1.width / 2)
      } else if (right !== null) {
        x = right - (b1.width / 2)
      }

      let y = b1.top + (b1.height / 2)
      if (top !== null) {
        y = top + (b1.height / 2)
      } else if (bottom !== null) {
        y = bottom - (b1.height / 2)
      }

      object.setPositionByOrigin({ x, y }, 'center', 'center')
    }
  },

  reposition: function () {
    if (!doneLoading) {
      if (timers.reposition) {
        clearTimeout(timers.reposition)
      }
      timers.reposition = setTimeout(() => { this.reposition() }, 100)
      return
    }

    canvas._objects.sort((a, b) => {
      const ap = a.position || 0
      const bp = b.position || 0
      return ap - bp
    })
    canvas.renderAll()
  },

  queueObject: function (object) {
    if (!object.actionKey) {
      return
    }
    objectQueue.push(object)
    if (!queueStarted) {
      this.processQueue()
    }
  },

  addObject: function (object, callback) {
    if (!history.performed(object)) {
      if (object.type === 'image' && object.isBackground) {
        this.setBackgroundImage(object.src, true)
        if (callback) {
          callback()
        }
        return
      }

      fabric.util.enlivenObjects([object], (objects) => {
        objects.forEach((o) => {
          canvas.add(o)
          this.addParentKey(o)
          this.reposition()
        })
        if (callback) {
          callback()
        }
      })
    }
  },

  updateObject: function (object) {
    if (!history.performed(object)) {
      this.removeObject(object)
      this.addObject(object)
    }
  },

  findObject: function (object) {
    let o = null
    canvas.getObjects(object.type).forEach((p) => {
      if (!o && p.key === object.key) {
        o = p
      }
    })

    // type may have changed (i.e. added text)
    // so need to search all objects
    if (o === null && objects.shapes.isA(object)) {
      canvas.getObjects().forEach((p) => {
        if (!o && p.key === object.key) {
          o = p
        }
      })
    }

    return o
  },

  removeObject: function (object) {
    if (!history.performed(object)) {
      const o = this.findObject(object)
      if (o) {
        canvas.remove(o)
      }
    }
  },

  getMode: function () {
    if (canvas && canvas.isDrawingMode) {
      return 'drawing'
    }

    if (shapeMode) {
      return shapeMode
    }
  },

  setMode: function (mode) {
    const options = {}

    if (mode === 'Pencil') {
      shapeMode = ''
      options.isDrawingMode = true
      canvas.freeDrawingBrush.color = this.stroke.colour || defaults.color
      canvas.freeDrawingBrush.width = this.stroke.width || defaults.strokeWidth
    } else {
      shapeMode = mode.toLowerCase()
      shape = null
      options.isDrawingMode = false
    }

    if (shapeMode) {
      options.selection = false
      if (shapeMode === 'pan') {
        options.defaultCursor = 'move'
        options.hoverCursor = 'move'
      } else if (shapeMode === 'text') {
        options.defaultCursor = 'text'
        options.hoverCursor = 'text'
      } else if (shapeMode === 'erase') {
        options.defaultCursor = 'default'
        options.hoverCursor = 'pointer'
      } else {
        options.defaultCursor = 'crosshair'
        options.hoverCursor = 'crosshair'
      }
    } else {
      options.selection = !options.isDrawingMode
      options.defaultCursor = 'default'
      options.hoverCursor = 'move'
    }

    if (filtered && doneLoading && shapeMode !== 'pan') {
      this.resetFilter()
    }

    canvas.set(options)
    canvas.discardActiveObject()
    canvas.renderAll()
  },

  clear: function () {
    db.clearBoard()
    canvas.clear()
  },

  restore: function () {
    db.restoreBoard()
  },

  undo: function () {
    const action = history.last()
    if (!action) {
      return
    }

    canvas.discardActiveObject()
    db.undo(action)
  },

  redo: function () {
    const action = history.lastUndo()
    if (!action) {
      return
    }

    canvas.discardActiveObject()
    db.redo(action)
  },

  saveIgnoredObject: function (object) {
    object.key = ''
    if (object.type === 'group') {
      object.forEachObject((o) => {
        o.key = ''
      })
    }

    if (object.position === undefined) {
      object.position = this.lastObjectPosition() + 1
    }

    const json = this.json(object)
    db.add(json)
    object.key = json.key

    this.addParentKey(object)
  },

  centerObjectInViewport: function (object) {
    const viewport = {
      width: canvas.vptCoords.tr.x - canvas.vptCoords.tl.x,
      height: canvas.vptCoords.bl.y - canvas.vptCoords.tl.y
    }

    object.set({
      left: canvas.vptCoords.tl.x + (viewport.width / 2) - (object.width * (object.scaleX || 1) / 2),
      top: canvas.vptCoords.tl.y + (viewport.height / 2) - (object.height * (object.scaleY || 1) / 2)
    })
  },

  moveObjectIntoViewport: function (object) {
    object.set({
      left: object.left + canvas.vptCoords.tl.x,
      top: object.top + canvas.vptCoords.tl.y
    })
  },

  addText: function (event, pointer) {
    const object = event ? canvas.findTarget(event) : null
    if (object) {
      if (objects.text.isA(object)) {
        return objects.text.enterEditing(object)
      }

      if (objects.note.isA(object)) {
        return this.editTextOnStickyNote(object)
      }

      if (objects.shapes.withText.isA(object)) {
        return this.editTextOnShape(object)
      }

      if (objects.shapes.supportsText(object)) {
        return this.addTextToShape(object)
      }
    }

    const text = objects.text.new(pointer, this.getTextSettings())
    text.key = db.ignoreKey
    text.isNew = true // prevents the i-text from triggering a save

    canvas.add(text)

    objects.text.edit(text, canvas, () => {
      text.isNew = false
      this.saveIgnoredObject(text)
      text.hasControls = false
      if (clickMenu) {
        clickMenu('Select')
      }
      canvas.setActiveObject(text)
    })
  },

  addTextToShape: function (shape) {
    // clone the shape to make it easier to cancel adding text to it
    shape.clone((clonedShape) => {
      const shapeWithText = objects.shapes.addText(clonedShape, this.getTextSettings())
      shapeWithText.key = shape.key

      canvas.remove(shape)
      canvas.add(shapeWithText)

      if (shape.position !== undefined) {
        shapeWithText.position = shape.position
        this.reposition()
      }

      objects.shapes.withText.editText(shapeWithText, canvas, db.ignoreKey, (ok) => {
        let currentShape = shapeWithText
        if (!ok) {
          canvas.remove(shapeWithText)
          canvas.add(shape)

          if (shape.position !== undefined) {
            this.reposition()
          }

          currentShape = shape
        }

        if (currentShape.key === db.ignoreKey) {
          this.saveIgnoredObject(currentShape)
          if (clickMenu && shapeMode === 'text') {
            clickMenu('Select')
          } else if (shapeMode !== 'text') {
            this.setMode(shapeMode)
          }
        } else {
          db.update(this.json(currentShape))
        }

        canvas.setActiveObject(currentShape)
        canvas.renderAll()
      })
    })
  },

  editTextOnShape: function (object) {
    objects.shapes.withText.editText(object, canvas, db.ignoreKey, () => {
      if (clickMenu && shapeMode === 'text') {
        clickMenu('Select')
      } else if (shapeMode !== 'text') {
        this.setMode(shapeMode)
      }
      canvas.setActiveObject(object)
      this.applyDelayedStyleSettings()
    })
  },

  addStickyNote: function () {
    const note = objects.note.new()
    note.key = db.ignoreKey

    this.centerObjectInViewport(note)

    canvas.add(note)

    shapeMode = 'text'
    objects.note.editText(note, canvas, db.ignoreKey, () => {
      this.saveIgnoredObject(note)

      if (clickMenu && shapeMode === 'text') {
        clickMenu('Select')
      } else if (shapeMode !== 'text') {
        this.setMode(shapeMode)
      }

      canvas.setActiveObject(note)
    })
  },

  editTextOnStickyNote: function (object) {
    objects.note.editText(object, canvas, db.ignoreKey, () => {
      if (clickMenu && shapeMode === 'text') {
        clickMenu('Select')
      } else if (shapeMode !== 'text') {
        this.setMode(shapeMode)
      }
      canvas.setActiveObject(object)
      this.applyDelayedStyleSettings()
    })
  },

  setObjectStrokeColour: function (object, colour) {
    if (object.stroke && !object.customType) {
      object.set({ stroke: colour })
    } else if (objects.shapes.line.isA(object)) {
      objects.shapes.line.setStroke(object, colour)
    } else if (objects.shapes.arrow.isA(object)) {
      objects.shapes.arrow.setStroke(object, colour)
    } else if (objects.shapes.withText.isA(object)) {
      objects.shapes.withText.setShapeStroke(object, colour)
    }
  },

  setObjectFillColour: function (object, colour) {
    if (objects.isChildTextbox(object)) {
      object = object.parent
    }

    if (objects.note.isA(object)) {
      objects.note.setNoteColour(object, colour)
    } else if (objects.shapes.withText.isA(object)) {
      objects.shapes.withText.setShapeColour(object, colour)
    } else if (!object.customType) {
      object.setColor(colour)
    }
  },

  setObjectStrokeWidth: function (object, width) {
    if (objects.isChildTextbox(object)) {
      object = object.parent
    }

    if (!object.customType) {
      object.set({ strokeWidth: width })
    } else if (objects.shapes.line.isA(object)) {
      objects.shapes.line.setStrokeWidth(object, width)
    } else if (objects.shapes.arrow.isA(object)) {
      objects.shapes.arrow.setStrokeWidth(object, width)
    } else if (objects.shapes.withText.isA(object)) {
      objects.shapes.withText.setShapeStrokeWidth(object, width)
    }
  },

  getText: function (object) {
    let text = null
    if (objects.isChildTextbox(object) || objects.text.isA(object)) {
      text = object
    } else if (objects.note.isA(object)) {
      text = objects.note.getText(object)
    } else if (objects.shapes.withText.isA(object)) {
      text = objects.shapes.withText.getText(object)
    }

    return text
  },

  setObjectTextSettings: function (object) {
    const text = this.getText(object)

    if (text) {
      text.set(this.getTextSettings())
    }
  },

  getTextSettings: function () {
    return {
      fill: this.text.colour,
      fontFamily: this.text.serif ? 'Times New Roman' : 'Arial',
      fontSize: this.text.size,
      fontStyle: this.text.italic ? 'italic' : 'normal',
      fontWeight: this.text.bold ? 'bold' : 'normal',
      underline: this.text.underline,
      textAlign: this.text.align
    }
  },

  applyStyleSettings: function () {
    const changedObjects = {}
    this.setFillColour(changedObjects)
    this.setStrokeColourAndWidth(changedObjects)
    this.setTextSettings(changedObjects)

    Object.keys(changedObjects).forEach((key) => {
      const object = changedObjects[key]
      if (key === db.ignoreKey && object.parent) {
        delayedStyleSettings.push({ type: object.parent.type, key: object.parent.key })
      } else {
        db.update(this.json(object))
      }
    })

    canvas.renderAll()
  },

  applyDelayedStyleSettings: function () {
    const updated = {}
    delayedStyleSettings.forEach((object) => {
      if (!updated[object.key]) {
        updated[object.key] = true
        object = this.findObject(object)
        if (object) {
          db.update(this.json(object))
        }
      }
    })

    delayedStyleSettings = []
  },

  setStrokeColourAndWidth: function (changedObjects) {
    this.setStrokeWidth(changedObjects)
    this.setStrokeColour(changedObjects)
  },

  setFillColour: function (changedObjects) {
    const fillColour = this.fill.none ? '' : this.fill.colour

    const object = this.getActiveObject()
    const group = this.getActiveGroup()

    if (object) {
      if (fillColour || objects.getStrokeWidth(object) !== 0) {
        this.setObjectFillColour(object, fillColour)
        changedObjects[object.key] = object
      }
    } else if (group) {
      // set colour of local version
      group.forEachObject((o) => {
        if (fillColour || objects.getStrokeWidth(o) !== 0) {
          this.setObjectFillColour(o, fillColour)
        }
      })

      // apply group transformation and update db
      group.clone((g) => {
        g.forEachObject((o) => {
          g.realizeTransform(o)
          if (fillColour || objects.getStrokeWidth(o) !== 0) {
            changedObjects[o.key] = o
          }
        })
      }, ['actionKey', 'key'])
    }

    canvas.renderAll()
  },

  setStrokeColour: function (changedObjects) {
    canvas.freeDrawingBrush.color = this.stroke.colour

    const object = this.getActiveObject()
    const group = this.getActiveGroup()

    if (object) {
      this.setObjectStrokeColour(object, this.stroke.colour)
      changedObjects[object.key] = object
    } else if (group) {
      // set colour of local version
      group.forEachObject((o) => {
        this.setObjectStrokeColour(o, this.stroke.colour)
      })

      // apply group transformation and update db
      group.clone((g) => {
        g.forEachObject((o) => {
          g.realizeTransform(o)
          changedObjects[o.key] = o
        })
      }, this.customProperties())
    }

    canvas.renderAll()
  },

  setTextSettings: function (changedObjects) {
    let object = this.getActiveObject()
    const group = this.getActiveGroup()

    // treat our custom types as objects
    if (group && group.customType) {
      object = group
    }

    if (object) {
      this.setObjectTextSettings(object)
      changedObjects[object.key] = object
    } else if (group) {
      // set colour of local version
      group.forEachObject((o) => {
        this.setObjectTextSettings(o)
      })

      // apply group transformation and update db
      group.clone((g) => {
        g.forEachObject((o) => {
          g.realizeTransform(o)
          if (objects.supportsText(o)) {
            changedObjects[o.key] = o
          }
        })
      }, ['actionKey', 'key'])
    }
  },

  setStrokeWidth: function (changedObjects) {
    canvas.freeDrawingBrush.width = this.stroke.width > 0 ? this.stroke.width : defaults.strokeWidth

    const object = this.getActiveObject()
    const group = this.getActiveGroup()

    if (object) {
      if (this.stroke.width !== 0 || (object.fill && object.type !== 'line')) {
        this.setObjectStrokeWidth(object, this.stroke.width)
        changedObjects[object.key] = object
      }
    } else if (group) {
      // set stroke of local version
      group.forEachObject((o) => {
        if (this.stroke.width !== 0 || (o.fill && o.type !== 'line')) {
          this.setObjectStrokeWidth(o, this.stroke.width)
        }
      })

      // apply group transformation and update db
      group.clone((g) => {
        g.forEachObject((o) => {
          g.realizeTransform(o)
          if (this.stroke.width !== 0 || (o.fill && o.type !== 'line')) {
            changedObjects[o.key] = o
          }
        })
      }, this.customProperties())
    }

    canvas.renderAll()
  },

  updateStyleSettings: function (settings) {
    let object = canvas.getActiveObject()

    if (!object) {
      // certain drawing modes do not support fill
      settings.supportsFill = this.getMode() !== 'arrow' && this.getMode() !== 'line' && this.getMode() !== 'drawing'
      return
    }

    if (object.type === 'activeSelection') {
      settings.supportsFill = false
      settings.supportsBorder = false
      settings.supportsText = false
      // for groups, check if any object within supports fill and border
      object.forEachObject((o) => {
        settings.supportsFill = objects.supportsFillColour(o) || settings.supportsFill
        settings.supportsBorder = objects.supportsBorderColour(o) || settings.supportsBorder
        settings.supportsText = objects.supportsText(o) || settings.supportsText
      })
      return
    }

    const text = this.getText(object)
    if (objects.isChildTextbox(object)) {
      object = object.parent
    }

    settings.supportsFill = objects.supportsFillColour(object)
    settings.supportsBorder = objects.supportsBorderColour(object)
    settings.supportsText = objects.supportsText(object)

    if (objects.note.isA(object)) {
      settings.fill.none = false
      settings.fill.colour = objects.note.getBox(object).fill
    } else if (objects.shapes.line.isA(object)) {
      settings.border.colour = objects.shapes.line.getStroke(object)
      settings.border.width = objects.shapes.line.getStrokeWidth(object)
    } else if (objects.shapes.arrow.isA(object)) {
      settings.border.colour = objects.shapes.arrow.getStroke(object)
      settings.border.width = objects.shapes.arrow.getStrokeWidth(object)
    } else {
      if (objects.shapes.withText.isA(object)) {
        settings.text.colour = text ? text.fill : settings.text.colour
        object = objects.shapes.withText.getShape(object)
      }
      settings.fill.none = object.fill === ''
      settings.fill.colour = !settings.fill.none ? object.fill : settings.fill.colour
      settings.border.colour = object.stroke ? object.stroke : settings.border.colour
      settings.border.width = object.stroke && object.strokeWidth ? object.strokeWidth : settings.border.width
    }

    if (text) {
      settings.text.colour = text.fill
      settings.text.size = text.fontSize
      settings.text.bold = text.fontWeight === 'bold'
      settings.text.italic = text.fontStyle === 'italic'
      settings.text.underline = text.underline
      settings.text.align = text.textAlign
      settings.text.serif = text.fontFamily !== 'Arial'
    }
  },

  setZoom: function (zoom, event) {
    if (!canvas) {
      return
    }

    if (this.hasBackgroundImage() && zoom < bgMinZoom) {
      return
    }

    if (event) {
      // use current pointer, or default to centre
      const pointer = canvas.getPointer(event, true)
      if (isNaN(pointer.x) || isNaN(pointer.y)) {
        pointer.x = canvas.width / 2
        pointer.y = canvas.height / 2
      }

      // zoom to the centre of the canvas
      canvas.zoomToPoint(pointer, zoom)
    } else {
      canvas.setZoom(zoom)
    }

    canvas.renderAll()
  },

  setPan: function (point) {
    if (!canvas) {
      return
    }

    // restrict pan to image bounds
    if (this.hasBackgroundImage()) {
      const w = -1 * ((canvas.getZoom() * canvas.backgroundImage.width) - canvas.getWidth())
      const h = -1 * ((canvas.getZoom() * canvas.backgroundImage.height) - canvas.getHeight())

      if (w === 0 && h === 0) {
        return
      }

      if (canvas.viewportTransform[4] > 0) {
        point.x = -canvas.viewportTransform[4]
      } else if (canvas.viewportTransform[4] < w) {
        point.x = w - canvas.viewportTransform[4]
      }

      if (canvas.viewportTransform[5] > 0) {
        point.y = -canvas.viewportTransform[5]
      } else if (canvas.viewportTransform[5] < h) {
        point.y = h - canvas.viewportTransform[5]
      }
    }

    const amount = new fabric.Point(point.x, point.y)
    canvas.relativePan(amount)
  },

  addImageFromURL: function (url, status, event, close, errorHandler, key = '') {
    status.error = false
    // use a placeholder until the image is loaded
    const placeHolder = objects.image.newPlaceHolder()
    placeHolder.key = db.ignoreKey
    canvas.add(placeHolder)

    this.centerObjectInViewport(placeHolder)

    canvas.renderAll()

    objects.image.new(url, canvas.getWidth(), canvas.getHeight(), (object) => {
      // make sure we have a valid image
      if (object.width > 0 && object.height > 0) {
        const padding = 25

        // scale to fit on canvas
        if (object.width * object.scaleX > canvas.width) {
          object.scaleToWidth(canvas.width - padding)
        }

        if (object.height * object.scaleY > canvas.height) {
          object.scaleToHeight(canvas.height - padding)
        }

        canvas.remove(placeHolder)

        object.key = key || db.ignoreKey
        canvas.add(object)
        this.centerObjectInViewport(object)
        object.setCoords()

        if (object.key === db.ignoreKey) {
          this.saveIgnoredObject(object)
        } else {
          db.update(this.json(object), { type: 'add' })
        }

        upload.logImage(url, object.key)

        status.done = true
        status.visible = true
        if (close) {
          close(true)
        }

        canvas.setActiveObject(object)
        canvas.renderAll()
      } else {
        status.error = true
        canvas.remove(placeHolder)
        errorHandler()
      }
    })
  },

  uploadFile: function (file, status, event, close, errorHandler) {
    if (fileType.isImage(file.type)) {
      db.uploadImage(file, status, (key, url) => {
        this.addImageFromURL(url, status, event, close, errorHandler, key)
      })
    } else {
      db.uploadFile(file, status, (key, url) => {
        this.addFile(file, url, status, event, close, key)
      })
    }
  },

  addFileFromURL: function (url, status, event, close, errorHandler, key = '') {
    upload.file(url).then((header) => {
      if (fileType.isImage(header.contentType)) {
        this.addImageFromURL(header.url, status, event, close, errorHandler, key)
      } else {
        const file = { name: header.fileName, type: header.contentType, size: header.contentLength }
        this.addFile(file, url, status, event, close, key)
      }
    }, () => {
      errorHandler()
    })
  },

  addFile: function (file, url, status, event, close, key = '') {
    status.error = false
    let pointer = null
    if (event) {
      pointer = canvas.getPointer(event)
    }

    objects.file.new({ url, name: file.name, type: file.type, size: file.size, stamp: file.lastModified }).then((object) => {
      object.key = key || db.ignoreKey
      canvas.add(object)
      if (pointer) {
        object.set({ left: pointer.x - (object.width * object.scaleX / 2), top: pointer.y - (object.height * object.scaleY / 2) })
      } else {
        this.centerObjectInViewport(object)
      }
      object.setCoords()

      if (object.key === db.ignoreKey) {
        this.saveIgnoredObject(object)
      } else {
        db.update(this.json(object), { type: 'add' })
      }

      upload.logFile(url, object.key)

      status.done = true
      close(true)

      canvas.setActiveObject(object)
      canvas.renderAll()
    })
  },

  startShape: function (event) {
    // if on top of another object, lock it to prevent moving
    if (shapeMode !== 'text') {
      const selection = canvas.getActiveObject()
      if (selection) {
        this.lockObject(selection)
      }
    }

    const pointer = canvas.getPointer(event)

    if (shapeMode === 'text') {
      return this.addText(event, pointer)
    }

    const fillColour = this.fill.none ? '' : this.fill.colour
    const strokeWidth = this.fill.none && !this.stroke.width ? defaults.strokeWidth : this.stroke.width

    shape = objects.shapes.new(shapeMode, pointer, fillColour, this.stroke.colour, strokeWidth)
    if (shape) {
      shape.key = db.ignoreKey
      canvas.add(shape)
    }
  },

  endShape: function (event) {
    // if on top of another object, make sure we unlock it
    const selection = canvas.getActiveObject()
    if (selection) {
      this.unlockObject(selection)
      canvas.discardActiveObject()
    }

    if (objects.shapes.visible(shape)) {
      shape.setCoords()

      // need to use temp shape, since click will set shape to null
      const tempShape = shape

      if (objects.shapes.supportsText(tempShape)) {
        if (clickMenu) {
          clickMenu('Text')
        }
        return this.addTextToShape(tempShape)
      }

      this.saveIgnoredObject(shape)

      if (clickMenu) {
        clickMenu('Select')
      } else {
        tempShape.hasControls = false
      }

      canvas.setActiveObject(tempShape)
    } else {
      canvas.remove(shape)
    }

    shape = null
  },

  updateShape: throttle(function (event) {
    if (!shape) {
      return
    }

    const pointer = canvas.getPointer(event)

    const options = objects.shapes.options(shapeMode, shape, pointer, event.shiftKey)
    if (options) {
      if (shape.type === 'group') {
        shape.set(options.group)
        shape.forEachObject((o, i) => {
          if (options.objects[i]) {
            o.set(options.objects[i])
          }
        })
      } else {
        shape.set(options)
      }

      canvas.renderAll()
    }
  }, 5, { leading: true, trailing: false }),

  isDrawing: function (event) {
    const mode = this.getMode()

    if (mode === 'pan') {
      return false
    }

    if (mode === 'drawing') {
      return event.buttons === 1
    }

    return !!mode
  },

  copy: function () {
    const object = canvas.getActiveObject()

    if (navigator.clipboard) {
      navigator.clipboard.writeText('')
    }

    if (!object) {
      return
    }

    if (objects.text.isEditing(object)) {
      return
    }

    object.clone((o) => {
      history.copy(o)
    }, this.historyProperties())
  },

  cut: function () {
    if (this.isEditingText()) {
      return
    }

    this.copy()
    this.delete()
  },

  pasteImageToFile: function (event) {
    return new Promise((resolve, reject) => {
      var items = (event.clipboardData || event.originalEvent.clipboardData).items
      // find pasted image among pasted items
      var blob = null
      for (var i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') === 0) {
          blob = items[i].getAsFile()
        }
      }

      // load image if there is a pasted image
      if (blob !== null) {
        var reader = new FileReader()
        reader.onload = function (event) {
          return fetch(event.target.result)
            .then(res => res.arrayBuffer())
            .then(buf => resolve(new File([buf], uuidv4(), { type: 'image/png' })))
            .catch(err => reject(err))
        }
        reader.readAsDataURL(blob)
      } else {
        reject('No image found.')
      }
    })
  },
  paste: function (event) {
    if (this.isEditingText()) {
      return
    }

    const object = history.paste(this.historyProperties())

    if (!object) {
      return
    }

    const options = {
      left: 5,
      top: 5,
      originX: 'left',
      originY: 'top'
    }

    if (object.type === 'activeSelection') {
      const group = object
      group.set(options)
      this.centerObjectInViewport(group)

      const objects = []
      group.forEachObject((o) => {
        group.realizeTransform(o)
        objects.push(o)
      })

      objects.forEach((o) => {
        group.remove(o)
        canvas.add(o)
        this.addParentKey(o)
      })

      canvas.discardActiveObject()
      canvas.setActiveObject(new fabric.ActiveSelection(objects, { canvas }))
    } else {
      object.set(options)
      this.centerObjectInViewport(object)
      canvas.add(object)
      canvas.setActiveObject(object)
      this.addParentKey(object)
    }
  },

  delete: function () {
    if (this.isEditingText()) {
      return
    }

    let object = this.getActiveObject()
    const group = this.getActiveGroup()

    canvas.discardActiveObject()

    // treat our custom types as objects
    if (group && group.customType) {
      object = group
    }

    if (object) {
      canvas.remove(object)
      db.remove(this.json(object))
    } else if (group) {
      if (group.customType) {}
      group.forEachObject((o) => {
        canvas.remove(o)
        db.remove(this.json(o))
      })
    }
  },

  sendForward: function () {
    return this.changePosition({ forward: true })
  },

  bringToFront: function () {
    return this.changePosition({ front: true })
  },

  sendBackwards: function () {
    return this.changePosition({ backwards: true })
  },

  sendToBack: function () {
    return this.changePosition({ back: true })
  },

  changeObjectPosition: function (object, options) {
    if (options.fowrard) {
      object.bringForward()
    }

    if (options.front) {
      object.position = this.lastObjectPosition() + 1
      object.bringToFront()
    }

    if (options.backwards) {
      object.sendBackwards()
    }

    if (options.back) {
      object.position = this.firstObjectPosition() - 1
      object.sendToBack()
    }
  },

  changePosition: function (options) {
    const object = this.getActiveObject()
    const group = this.getActiveGroup()

    if (object) {
      this.changeObjectPosition(object, options)
      db.update(this.json(object))
    } else if (group) {
      // get each object directly from the canvas
      const local = []
      group.forEachObject((o) => {
        local.push(this.findObject({ type: o.type, key: o.key }))
      })

      // need to discard the select, otherwise position change doesn't seem to work
      canvas.discardActiveObject()

      local.forEach((o) => {
        this.changeObjectPosition(o, options)
        db.update(this.json(o))
      })
    }

    canvas.discardActiveObject()
    canvas.renderAll()
  },

  handleDoubleClick: function (object) {
    if (!object) {
      return
    }

    if (shapeMode === 'pan') {
      if (clickMenu) {
        clickMenu('Select')
      }
      canvas.setActiveObject(object)
      canvas.renderAll()
      return
    }

    if (objects.note.isA(object)) {
      this.editTextOnStickyNote(object)
    }

    if (objects.shapes.withText.isA(object)) {
      this.editTextOnShape(object)
    } else if (objects.shapes.supportsText(object)) {
      this.addTextToShape(object)
    }
  },

  hasSelection: function () {
    return !!canvas.getActiveObject()
  },

  export: function (status, migrate) {
    const options = { format: 'jpeg', quality: 0.8, multiplier: 1 }

    if (migrate) {
      this.migrateTextFix(canvas._objects)
    }

    const maxSize = 5000

    const width = canvas.getWidth()
    const height = canvas.getHeight()

    if (width >= height && width > maxSize) {
      options.multiplier = maxSize / width
    } else if (height > maxSize) {
      options.multiplier = maxSize / height
    } else if (width * 2 < maxSize && height * 2 < maxSize) {
      options.multiplier = 2
    }

    return canvas.toDataURL(options)
  },

  migrateTextFix: function (objects) {
    for (let i = 0; i < objects.length; i++) {
      const type = objects[i].type
      switch (type) {
        case 'i-text':
        case 'textbox':
          if (objects[i].fontFamily === 'Arial') {
            objects[i].fontFamily = 'sans-serif'
          }
          break
        case 'group':
          this.migrateTextFix(objects[i]._objects)
          break
      }
    }
  },

  lockObject: function (object, lock = true) {
    return object.set({
      hasControls: !lock,
      hasBorders: !lock,
      lockMovementX: lock,
      lockMovementY: lock,
      lockScalingX: lock,
      lockScalingY: lock,
      lockUniScaling: lock,
      lockRotation: lock
    })
  },

  unlockObject: function (object) {
    return this.lockObject(object, false)
  },

  readOnly: function () {
    canvas.set({
      isDrawingMode: false,
      selection: false,
      defaultCursor: 'pointer',
      hoverCursor: 'pointer'
    })

    canvas.discardActiveObject()
    canvas.renderAll()

    canvas.on('object:added', (options) => {
      if (doneLoading) {
        this.zoomToFit()
      }
    })

    canvas.on('object:modified', (options) => {
      if (doneLoading) {
        this.zoomToFit()
      }
    })
  },

  makeObjectReadOnly: function (object) {
    object.set({
      selectable: false,
      evented: false,
      hasControls: false
    })
  },

  getContentCoords: function () {
    const coords = { x1: null, y1: null, x2: null, y2: null }
    if (canvas._objects.length > 0) {
      coords.x1 = coords.y1 = Number.MAX_SAFE_INTEGER
      canvas.forEachObject((o) => {
        const corners = o.aCoords
        coords.x1 = Math.min(corners.tl.x, corners.tr.x, corners.bl.x, corners.br.x, coords.x1 !== null ? coords.x1 : corners.tl.x)
        coords.y1 = Math.min(corners.tl.y, corners.tr.y, corners.bl.y, corners.br.y, coords.y1 !== null ? coords.y1 : corners.tl.y)
        coords.x2 = Math.max(corners.tl.x, corners.tr.x, corners.bl.x, corners.br.x, coords.x2 !== null ? coords.x2 : corners.tl.x)
        coords.y2 = Math.max(corners.tl.y, corners.tr.y, corners.bl.y, corners.br.y, coords.y2 !== null ? coords.y2 : corners.tl.y)
      })
    }

    return coords
  },

  zoomToFit: function (minZoom, step) {
    if (this.hasBackgroundImage()) {
      canvas.setZoom(bgMinZoom)
      this.origin()
      return bgMinZoom
    }

    canvas.setZoom(1)

    const coords = this.getContentCoords()
    canvas.absolutePan(new fabric.Point(coords.x1, coords.y1))
    const zoomX = canvas.width / Math.abs(coords.x2 - coords.x1)
    const zoomY = canvas.height / Math.abs(coords.y2 - coords.y1)

    let zoom = 1
    if (!readOnly) {
      if (zoomX < 1 || zoomY < 1) {
        zoom = Math.round(Math.min(zoomX, zoomY) / step * (step * 100)) / 100
        zoom = Math.max(zoom, minZoom)
      }
      this.setZoom(zoom)
    } else {
      zoom = Math.min(zoomX, zoomY)
      canvas.setZoom(zoom)
    }
    return zoom
  },

  centre: function () {
    if (canvas._objects.length === 0) {
      return
    }

    const coords = this.getContentCoords()

    const zoom = canvas.getZoom()

    const width = (coords.x2 - coords.x1) * zoom
    const height = (coords.y2 - coords.y1) * zoom

    canvas.absolutePan(new fabric.Point(coords.x1 * zoom, coords.y1 * zoom))

    if (width < canvas.getWidth() || height < canvas.getHeight()) {
      const centre = canvas.getCenter()
      canvas.relativePan(new fabric.Point(
        centre.left - (width / 2),
        centre.top - (height / 2)
      ))
    }
  },

  origin: function () {
    canvas.absolutePan(new fabric.Point(0, 0))
  },

  isEditingText: function () {
    return objects.text.isEditing(canvas.getActiveObject())
  },

  timeSinceLastUpdate: function () {
    if (!lastUpdate) {
      return 0
    }

    return new Date() - lastUpdate
  },

  numExpectedObjects: function () {
    return db.numExpectedObjects()
  },

  numLoadedObjects: function () {
    return db.numLoadedObjects()
  },

  afterLoaded: function (callback, count = 0) {
    const timeSince = this.timeSinceLastUpdate()

    // handle case where objects were added and our
    // listeners didn't catch it... would cause the board
    // to never finish loading
    if (objectStatus.added < canvas._objects.length) {
      objectStatus.added = canvas._objects.length
    }

    // prevent failed images causing us to not finish loading
    if (objects.image.failed()) {
      this.addImagePlaceHolders(objects.image.popFailed())
    }

    let wait = false
    if (newBoard) {
      // don't wait
    } else if (count === 0) {
      wait = true // always wait at least once
    } else if (objectStatus.expected === this.expectedNotLoaded) {
      wait = true // metadata isn't loaded yet
    } else if (objectQueue.length > 0) {
      wait = true // we have objects to add still
    } else if (objectStatus.added < objectStatus.expected) {
      wait = timeSince < 3000 // we haven't added everything, and it's been < 3 secs since the last object was added
    } else if (waitForImages) {
      wait = objects.image.pending()
    }

    if (wait) {
      if (timers.afterLoaded) {
        clearTimeout(timers.afterLoaded)
      }
      timers.afterLoaded = setTimeout(() => {
        this.afterLoaded(callback, count + 1)
      }, 500)
    } else {
      doneLoading = true
      callback()
    }
  },

  erase: function () {
    if (this.isEditingText()) {
      return
    }
    this.delete()
  },

  filter: function (filter, callback) {
    canvas.forEachObject((o) => {
      if (filter.keys.indexOf(o.key) === -1) {
        o.opacity = 0.1
        filtered = true
      } else {
        o.opacity = 1
      }
    })
    this.zoomToFit(0.1, 0.05)
    callback()
  },

  resetFilter: function () {
    filtered = false
    canvas.forEachObject((o) => {
      o.opacity = 1
    })
    if (this.resetFilterCallback) {
      this.resetFilterCallback()
    }
  },

  processQueue: function (emptyCount) {
    if (objectQueue.length > 0) {
      queueStarted = true
      for (let i = 0; i < 3; i++) {
        if (objectQueue.length > 0) {
          this.addObject(objectQueue.shift())
        }
      }
      setTimeout(() => { this.processQueue() }, 25)
    } else {
      queueStarted = false
    }
  },

  resetTimers: function () {
    Object.keys(timers).forEach((key) => {
      if (timers[key]) {
        clearTimeout(timers[key])
      }
    })
    timers = {}
  },

  getActiveObject: function () {
    const object = canvas.getActiveObject()
    return object && object.type !== 'activeSelection' ? object : null
  },

  getActiveGroup: function () {
    const object = canvas.getActiveObject()
    return object && object.type === 'activeSelection' ? object : null
  },

  previewIsUpToDate: function (config) {
    return db.previewIsUpToDate({ id: config.boardKey })
  },

  getPreviewURL: function (config) {
    return db.getPreviewURL({ id: config.boardKey })
  },

  generatePreview: function (status, migrate) {
    // make sure objects are in the correct order
    this.reposition()

    // make canvas full size
    if (this.hasBackgroundImage()) {
      canvas.setZoom(1)
      canvas.setDimensions({ width: canvas.backgroundImage.width, height: canvas.backgroundImage.height })
      canvas.absolutePan({ x: 0, y: 0 })
    } else {
      const padding = 10
      canvas.setZoom(1)
      const coords = this.getContentCoords()
      canvas.setWidth(coords.x2 - coords.x1 + padding)
      canvas.setHeight(coords.y2 - coords.y1 + padding)
      canvas.absolutePan({ x: coords.x1 - (padding / 2), y: coords.y1 - (padding / 2) })
    }

    return new Promise((resolve, reject) => {
      resolve(this.export(status, migrate))
    })
  },
  setHistory: function (enabled) {
    history.enabled = enabled
  },
  usingTemplate: function () {
    for (let i = 0; i < canvas._objects.length; i++) {
      if (canvas._objects[i] && canvas._objects[i].fromTemplate === true) {
        return true
      }
    }
    return false
  },

  addImagePlaceHolders: function (configs) {
    configs.forEach((config) => {
      const placeHolder = objects.image.newPlaceHolder(config)
      placeHolder.key = db.ignoreKey
      this.makeObjectReadOnly(placeHolder)
      canvas.add(placeHolder)
    })
    canvas.renderAll()
  },

  setBackgroundImage: function (url, existing) {
    canvas.backgroundColor = ''
    return new Promise((resolve) => {
      fabric.Image.fromURL(url, (image) => {
        const size = {
          width: image.width,
          height: image.height
        }
        canvas.absolutePan(new fabric.Point(0, 0))
        canvas.setBackgroundImage(image, () => {
          if (!existing) {
            image.isBackground = true
            const json = this.json(image)
            db.add(json)
            image.key = json.key
            image.actionKey = json.key
          }
          objectStatus.added++
          canvas.setDimensions(size)
          canvas.renderAll.bind(canvas)

          resolve(size)
        }, {
          originX: 'left',
          originY: 'top',
          crossOrigin: 'anonymous'
        })
      }, {
        crossOrigin: 'anonymous'
      })
    })
  },

  hasBackgroundImage: function () {
    return canvas && !!canvas.backgroundImage
  },

  getBackgroundImageSize: function () {
    return {
      width: canvas.backgroundImage.width,
      height: canvas.backgroundImage.height
    }
  },

  scaleBackgroundImage: function (zoom) {
    bgMinZoom = zoom
    canvas.setZoom(zoom)
  },

  hasImages: function () {
    for (let i = 0; i < canvas._objects.length; i++) {
      if (objects.image.isA(canvas._objects[i])) {
        return true
      }
    }

    return false
  },

  findObjectByKeyAndType: function (key, type) {
    if (type === 'images') {
      type = 'image'
    }

    return new Promise((resolve, reject) => {
      const o = this.findObject({ type, key })
      if (o && (!o.customType)) {
        return resolve(o)
      }

      db.getItemFromHistory(key, (object) => {
        fabric.util.enlivenObjects([object], (objects) => {
          objects.forEach((o) => {
            o.deleted = true
            return resolve(o)
          })
        })
      }, () => {
        reject()
      })
    })
  },
  numberOfObjects: function () {
    return canvas._objects.length
  },
  isDirty: function () {
    return history.isDirty()
  },
  finalize: function () {
    if (this.numberOfObjects() === 0) {
      return
    }

    // make sure any ignored objects are saved
    canvas._objects.forEach((object) => {
      if (object.key === db.ignoreKey) {
        if (objects.shapes.withText.isText(object)) {
          object.exitEditing()
        } else {
          this.saveIgnoredObject(object)
        }
      }
    })
  }
}
